import { Box, Typography } from '@mui/material';
import { CaretRight } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { UserService } from '../../Service/UserService';
import { User } from '../../Utilis/Types/User';
import { useAppDispatch } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import { useSidebarExtension } from '../../context/SidebarExtensionProvider/useSidebarExtension';
import { ReportCategory } from '../../Utilis/Types/Report';
import { Steps } from '../../components/Steps';
import { ReportService } from '../../Service/ReportService';
import { Document } from '../../components/Document';
import { debounce } from 'lodash';
import { Report } from '../../Utilis/Types/Report';
import { UploadDocument } from '../../components/UploadDocument';
import { Loading } from '../../components/Loading';
import axios from 'axios';
import { Buffer } from 'buffer';

const reportCategories = {
  [ReportCategory.MASTER_TAX_PLAN]: 'Master Tax Plan',
  [ReportCategory.MASTER_FINANCIAL_PLAN]: 'Master Financial Plan',
  [ReportCategory.REDTAIL_DOCUMENT]: 'RedTail Documents',
};

const AdminReports = () => {
  const { userId } = useParams();
  const [clientDetails] = useOutletContext<any>();
  const [reportCategory, setReportCategory] = useState<ReportCategory>(
    ReportCategory.MASTER_TAX_PLAN
  );
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();
  const [reports, setReports] = useState<Report[]>([]);
  const { openSidebarExtension, closeSidebarExtension } = useSidebarExtension();

  useEffect(() => {
    const types = Object.values(ReportCategory);

    const sidebar = types.map(item => ({
      title: reportCategories[item],
      selected: reportCategory === item,
      endIcon: <CaretRight />,
      onClick: () => setReportCategory(item),
    }));

    openSidebarExtension(sidebar);
    getReports();
  }, [reportCategory]);

  const getReports = async () => {
    setIsLoading(true);
    try {
      const response = await ReportService.getReportsAdmin(
        reportCategory,
        userId!
      );

      setReports(response.data);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong , try again later!',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const uploadReport = async (file?: File) => {
    try {
      const response = await ReportService.uploadReport(
        reportCategory,
        userId!,
        file!
      );

      const newReports: Report[] = [response.data];
      setReports(newReports);
      appDispatch(
        createNotification({
          type: 'success',
          message: 'Report uploaded successfully!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Report upload failed!',
        })
      );
    }
  };

  const downloadReport = async (report: Report) => {
    if (report.category === ReportCategory.REDTAIL_DOCUMENT) {
      const result = await ReportService.downloadReport(report.id);

      const blob = new Blob([Buffer.from(result.data.buffer)], {
        type: 'application/pdf',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = report.title + '.pdf';
      link.click();

      return;
    }

    const url = new URL(report.src!);
    const file = url.pathname.split('/').pop();
    const filename = file!.split('.')[0];

    const link = document.createElement('a');

    axios
      .get(report.src!, {
        responseType: 'blob',
      })
      .then(res => {
        link.href = URL.createObjectURL(res.data);
        link.download = filename;
        link.click();
      })
      .finally(() => {
        link.remove();
      });
  };

  const showReport = async (report: Report) => {
    let src = report.src!;

    if (report.category === ReportCategory.REDTAIL_DOCUMENT) {
      const result = await ReportService.downloadReport(report.id);

      const blob = new Blob([Buffer.from(result.data.buffer)], {
        type: 'application/pdf',
      });

      src = URL.createObjectURL(blob);
    }

    window.open(src);
  };

  const deleteReport = async (report: Report) => {
    try {
      await ReportService.deleteReport(report.id);

      const newReports = reports.filter(data => data.id !== report.id);
      setReports(newReports);
      appDispatch(
        createNotification({
          type: 'success',
          message: 'Report deleted successfully!',
        })
      );
    } catch (e) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Report deletion failed!',
        })
      );
    } finally {
    }
  };

  const changeReportVisibility = async (report: Report, visible: boolean) => {
    const response = await ReportService.changeReportVisibility(
      report.id,
      visible
    );

    const prevReports = [...reports];
    const reportIndex = prevReports.findIndex(
      report => report.id === response.data.id
    );

    prevReports[reportIndex] = response.data;

    setReports(prevReports);
  };

  const debounceOnChangeVisibility = debounce(changeReportVisibility, 300);

  const steps = useMemo(
    () => [
      `${clientDetails?.name} ${clientDetails?.lastName}`,
      'Reports',
      reportCategories[reportCategory],
    ],
    [clientDetails, reportCategory]
  );

  const displayedReports = useMemo(() => {
    if (!reports.length && reportCategory !== ReportCategory.REDTAIL_DOCUMENT) {
      return (
        <UploadDocument
          title={reportCategories[reportCategory]}
          onChange={uploadReport}
        />
      );
    } else {
      return reports.map((report: Report) => {
        const isRedtailDocument =
          report.category === ReportCategory.REDTAIL_DOCUMENT;

        return (
          <Document
            key={report.id}
            title={report.title}
            visible={report.visible}
            uploadedAt={report.createdAt}
            onChangeVisibility={v => debounceOnChangeVisibility(report, v)}
            onDownload={() => downloadReport(report)}
            onDisplay={() => showReport(report)}
            onDelete={() => deleteReport(report)}
            toggleVisibility={isRedtailDocument}
            delete={!isRedtailDocument}
            display
            download
          />
        );
      });
    }
  }, [reports, reportCategory]);

  return (
    <Box padding='36px'>
      <Steps items={steps} />

      <Box paddingTop='36px'>
        <Typography
          sx={{
            color: 'var(--spaces-colors-text-colors-primary-text, #151219)',
            fontFamily: 'Inter',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            fontFeatureSettings: "'clig' off, 'liga' off",
            marginBottom: '32px',
          }}
        >
          {reportCategories[reportCategory]}
        </Typography>
        <Loading isLoading={isLoading}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {displayedReports}
          </Box>
        </Loading>
      </Box>
    </Box>
  );
};

export default AdminReports;
